import React, { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const CourageSpecsEditor = () => {
  const [form, setForm] = useState({
    // BUTTON
    buttonEn: "",
    buttonAr: "",
    buttonKu: "",

    // CAMPAIGN
    campaignLabelEn: "",
    campaignLabelAr: "",
    campaignLabelKu: "",

    campaignTitleEn: "",
    campaignTitleAr: "",
    campaignTitleKu: "",

    campaignPriceLabelEn: "",
    campaignPriceLabelAr: "",
    campaignPriceLabelKu: "",

    campaignPriceValue: "",
    campaignButtonText: "",
  });

  const [icons, setIcons] = useState([]);
  const [campaignImage, setCampaignImage] = useState(null);

  const [loading, setLoading] = useState(true);

  // MODAL
  const [showModal, setShowModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [replaceForIconUrl, setReplaceForIconUrl] = useState(null);
  const [uploadType, setUploadType] = useState("icon");

  useEffect(() => {
    const fetchSpecs = async () => {
      try {
        const [en, ar, ku] = await Promise.all([
          axios.get(`${API_BASE}/api/VoyahCourageSpecs?lang=en`),
          axios.get(`${API_BASE}/api/VoyahCourageSpecs?lang=ar`),
          axios.get(`${API_BASE}/api/VoyahCourageSpecs?lang=ku`),
        ]);

        setForm({
          buttonEn: en.data.button,
          buttonAr: ar.data.button,
          buttonKu: ku.data.button,

          campaignLabelEn: en.data.campaign.label,
          campaignLabelAr: ar.data.campaign.label,
          campaignLabelKu: ku.data.campaign.label,

          campaignTitleEn: en.data.campaign.title,
          campaignTitleAr: ar.data.campaign.title,
          campaignTitleKu: ku.data.campaign.title,

          campaignPriceLabelEn: en.data.campaign.priceLabel,
          campaignPriceLabelAr: ar.data.campaign.priceLabel,
          campaignPriceLabelKu: ku.data.campaign.priceLabel,

          campaignPriceValue: en.data.campaign.priceValue,
          campaignButtonText: en.data.campaign.button,
        });

        setIcons(en.data.icons || []);
        setCampaignImage(en.data.campaign.image);

        setLoading(false);
      } catch (err) {
        console.error("Failed to load Courage Specs:", err);
      }
    };

    fetchSpecs();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const resolveUrl = (url) => (!url ? "" : url.startsWith("http") ? url : `${API_BASE}${url}`);

  // =================================================
  // SAVE TEXT
  // =================================================
  const handleSave = async () => {
    const token = localStorage.getItem("token");

    try {
      await axios.put(`${API_BASE}/api/VoyahCourageSpecs/1`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("Specs updated!");
    } catch (err) {
      console.error(err);
      alert("Failed to update.");
    }
  };

  // =================================================
  // DELETE ICON
  // =================================================
  const handleDeleteIcon = async (url) => {
    const token = localStorage.getItem("token");

    try {
      await axios.delete(`${API_BASE}/api/VoyahCourageSpecs/1/image`, {
        params: { url },
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("Icon deleted!");
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Delete failed.");
    }
  };

  // =================================================
  // UPLOAD NEW ICON(s)
  // =================================================
  const handleUploadIcons = async () => {
    if (!uploadFile) return alert("Select file(s).");

    const token = localStorage.getItem("token");

    const fd = new FormData();
    fd.append("IconImages", uploadFile);
    fd.append("ReplaceIcons", false);

    try {
      await axios.post(`${API_BASE}/api/VoyahCourageSpecs/1/media`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("Icon uploaded!");
      setShowModal(false);
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Upload failed.");
    }
  };

  // =================================================
  // REPLACE ICON
  // =================================================
  const handleReplaceIcon = async () => {
    if (!uploadFile) return;

    const token = localStorage.getItem("token");

    try {
      await axios.delete(`${API_BASE}/api/VoyahCourageSpecs/1/image`, {
        params: { url: replaceForIconUrl },
        headers: { Authorization: `Bearer ${token}` },
      });

      const fd = new FormData();
      fd.append("IconImages", uploadFile);
      fd.append("ReplaceIcons", false);

      await axios.post(`${API_BASE}/api/VoyahCourageSpecs/1/media`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("Icon replaced!");
      setShowModal(false);
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Replace failed.");
    }
  };

  // =================================================
  // REPLACE CAMPAIGN IMAGE
  // =================================================
  const handleReplaceCampaignImage = async () => {
    if (!uploadFile) return;

    const token = localStorage.getItem("token");

    const fd = new FormData();
    fd.append("CampaignImage", uploadFile);

    try {
      await axios.post(`${API_BASE}/api/VoyahCourageSpecs/1/media`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("Campaign image updated!");
      setShowModal(false);
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Failed to replace image.");
    }
  };

  if (loading) return <p>Loading...</p>;

  return (
    <div className="container py-4">
      <h2 className="fw-bold mb-4">Courage Specs Editor</h2>

      {/* =====================================
          LANG GROUPS
       ===================================== */}
      <div className="row">
        {["En", "Ar", "Ku"].map((lang) => (
          <div className="col-lg-4 col-md-6 mb-4" key={lang}>
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <h5 className="text-primary mb-3">{lang} Version</h5>

                {/* BUTTON */}
                <label className="form-label">Button Text</label>
                <input
                  className="form-control mb-3"
                  name={`button${lang}`}
                  value={form[`button${lang}`]}
                  onChange={handleChange}
                />

                {/* CAMPAIGN */}
                <label className="form-label">Campaign Label</label>
                <input
                  className="form-control mb-3"
                  name={`campaignLabel${lang}`}
                  value={form[`campaignLabel${lang}`]}
                  onChange={handleChange}
                />

                <label className="form-label">Campaign Title</label>
                <textarea
                  rows="3"
                  className="form-control mb-3"
                  name={`campaignTitle${lang}`}
                  value={form[`campaignTitle${lang}`]}
                  onChange={handleChange}
                />

                <label className="form-label">Price Label</label>
                <input
                  className="form-control mb-3"
                  name={`campaignPriceLabel${lang}`}
                  value={form[`campaignPriceLabel${lang}`]}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* PRICE VALUE + BUTTON */}
      <div className="row mb-5">
        <div className="col-lg-6">
          <label className="form-label">Campaign Price Value</label>
          <input
            className="form-control"
            name="campaignPriceValue"
            value={form.campaignPriceValue}
            onChange={handleChange}
          />
        </div>

        <div className="col-lg-6">
          <label className="form-label">Campaign Button Text</label>
          <input
            className="form-control"
            name="campaignButtonText"
            value={form.campaignButtonText}
            onChange={handleChange}
          />
        </div>
      </div>

      {/* SAVE BUTTON */}
      <div className="text-end mb-5">
        <button className="btn btn-primary px-4" onClick={handleSave}>
          Save All Changes
        </button>
      </div>

      {/* =====================================
          ICONS TABLE
       ===================================== */}
      <h3 className="mb-3">Icons</h3>

      <button
        className="btn btn-success mb-3"
        onClick={() => {
          setUploadType("icon");
          setReplaceForIconUrl(null);
          setShowModal(true);
        }}
      >
        + Add New Icon
      </button>

      <table className="table table-bordered">
        <thead className="table-light">
          <tr>
            <th>Preview</th>
            <th>Title</th>
            <th>Description</th>
            <th style={{ width: "220px" }}>Actions</th>
          </tr>
        </thead>

        <tbody>
          {icons.map((ic, i) => (
            <tr key={i}>
              <td>
                <img
                  src={resolveUrl(ic.icon)}
                  style={{ maxHeight: "120px" }}
                  className="img-fluid rounded"
                />
              </td>
              <td>{ic.title}</td>
              <td>{ic.description}</td>
              <td className="d-flex flex-column gap-2">
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDeleteIcon(ic.icon)}
                >
                  Delete
                </button>

                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    setUploadType("icon");
                    setReplaceForIconUrl(ic.icon);
                    setShowModal(true);
                  }}
                >
                  Replace Icon
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* =====================================
          CAMPAIGN IMAGE
       ===================================== */}
      <h3 className="mt-5 mb-3">Campaign Image</h3>

      <img
        src={resolveUrl(campaignImage)}
        className="img-fluid rounded mb-3"
        style={{ maxHeight: "240px" }}
      />

      <button
        className="btn btn-primary"
        onClick={() => {
          setUploadType("campaign");
          setShowModal(true);
        }}
      >
        Replace Campaign Image
      </button>

      {/* =====================================
          MODAL
       ===================================== */}
      {showModal && (
        <>
          <div className="modal fade show" style={{ display: "block" }}>
            <div className="modal-dialog">
              <div className="modal-content shadow">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {uploadType === "campaign"
                      ? "Replace Campaign Image"
                      : replaceForIconUrl
                      ? "Replace Icon"
                      : "Upload New Icon"}
                  </h5>
                  <button className="btn-close" onClick={() => setShowModal(false)}></button>
                </div>

                <div className="modal-body">
                  <input
                    type="file"
                    className="form-control"
                    accept="image/*"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                  />
                </div>

                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>

                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      if (uploadType === "campaign") handleReplaceCampaignImage();
                      else if (replaceForIconUrl) handleReplaceIcon();
                      else handleUploadIcons();
                    }}
                  >
                    Upload
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default CourageSpecsEditor;
